/**
 * arbi session list — list active sessions
 */

import { Command } from 'commander'
import { sessions as sessionOps } from '@arbidocs/sdk'
import {
  runAction,
  resolveAuth,
  printTable,
  printJson,
  suggestSubcommandAndExit,
} from '../helpers.js'

function humanizeTtl(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return ''
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`
  return `${Math.round(seconds / 86400)}d`
}

export function registerSessionCommand(program: Command): void {
  const session = program.command('session').description('List and manage sessions')

  session
    .command('list')
    .description('List active sessions')
    .option('--json', 'Output as JSON (recommended for scripting / agents)')
    .action((opts?: { json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const data = await sessionOps.listSessions(arbi)

        if (opts?.json) {
          printJson(data)
          return
        }

        if (data.length === 0) {
          process.stderr.write('No active sessions.\n')
          return
        }

        printTable(
          [
            {
              header: 'ID',
              width: 18,
              value: (r) => (r.external_id as string) || (r.session_id as string),
            },
            { header: 'NAME', width: 20, value: (r) => (r.name as string) || '' },
            { header: 'STATUS', width: 12, value: (r) => r.status as string },
            {
              header: 'TTL',
              width: 10,
              // Raw `<n>s` was unreadable past a few hours; humanize once the
              // window grows beyond a minute.
              value: (r) => humanizeTtl(r.ttl as number),
            },
            {
              header: 'WORKSPACES',
              width: 12,
              value: (r) => String((r.workspaces as string[])?.length ?? 0),
            },
          ],
          data
        )
      })()
    )

  // Default action: list, OR suggest a subcommand if user typed a typo.
  session.arguments('[maybeSubcommand]').action(async (maybe?: string) => {
    if (maybe) {
      suggestSubcommandAndExit(
        'session',
        maybe,
        session.commands.map((c) => c.name())
      )
    }
    await session.commands.find((c) => c.name() === 'list')!.parseAsync([], { from: 'user' })
  })
}
